import React from 'react';
import { Link } from 'react-router-dom';

const steps = [
  {
    id: 'parents',
    number: '01',
    eyebrow: 'Drop-off & pick-up',
    title: 'Parents check in on their own',
    description:
      'Families scan a QR code or bookmark a page and check children in or out themselves—no clipboard, no waiting on the front desk.',
    imageSrc: '/how-it-works/parent-self-serve.png',
    imageAlt:
      'Parent attendance screen showing a student checked in with signature and check-in / check-out actions',
  },
  {
    id: 'daily-report',
    number: '02',
    eyebrow: 'Daily attendance',
    title: 'See who is here—and fix gaps fast',
    description:
      'Staff open a daily report for every student. Filter by grade or room, and override check-in or check-out when a parent forgot but did drop off or pick up.',
    imageSrc: '/how-it-works/daily-attendance.png',
    imageAlt:
      'Attendance records table with check-in and check-out times and manual override buttons',
  },
  {
    id: 'staff-access',
    number: '03',
    eyebrow: 'Your team',
    title: 'Share check-in access with staff',
    description:
      'Generate a short confirmation code so teachers and aides can request access. Approve or revoke anytime—staff can manage check-in / check-out without touching students or billing.',
    imageSrc: '/how-it-works/staff-access.png',
    imageAlt:
      'School profile staff access tab with confirmation code and approved staff list',
  },
] as const;

const HowItWorksPage = () => {
  return (
    <main className="relative z-0 mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-10 max-w-2xl">
        <p className="inline-flex rounded-full bg-skysoft/50 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
          How it works
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">
          Three gentle steps from door to day
        </h1>
        <p className="mt-4 font-sans text-lg text-ink/70">
          OnTime keeps mornings calm: parents self-serve, staff see the whole day at
          a glance, and your team can help without extra busywork.
        </p>
      </div>

      <nav
        className="mb-12 flex flex-wrap gap-2"
        aria-label="How it works steps"
      >
        {steps.map((step) => (
          <a
            key={step.id}
            href={`#${step.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 font-sans text-sm font-semibold text-ink/80 ring-2 ring-ink/10 transition hover:bg-skysoft/40 hover:text-ink hover:ring-ink/15"
          >
            <span className="font-display text-xs text-ink/45">{step.number}</span>
            {step.eyebrow}
          </a>
        ))}
      </nav>

      <div className="space-y-16 sm:space-y-20">
        {steps.map((step, index) => {
          const imageOnRight = index % 2 === 0;
          return (
            <section
              key={step.id}
              id={step.id}
              className="scroll-mt-28"
              aria-labelledby={`${step.id}-heading`}
            >
              <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-10">
                <div
                  className={`lg:col-span-5 ${
                    imageOnRight ? '' : 'lg:order-2'
                  }`}
                >
                  <p className="font-display text-sm font-semibold uppercase tracking-wider text-ink/45">
                    Step {step.number}
                  </p>
                  <p className="mt-2 inline-flex rounded-full bg-grass/40 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
                    {step.eyebrow}
                  </p>
                  <h2
                    id={`${step.id}-heading`}
                    className="mt-4 font-display text-2xl font-semibold leading-snug text-ink sm:text-3xl"
                  >
                    {step.title}
                  </h2>
                  <p className="mt-3 font-sans text-base leading-relaxed text-ink/70">
                    {step.description}
                  </p>
                </div>
                <div
                  className={`lg:col-span-7 ${
                    imageOnRight ? '' : 'lg:order-1'
                  }`}
                >
                  <figure className="overflow-hidden rounded-3xl border-[3px] border-ink/15 bg-white shadow-[4px_6px_0_rgba(44,44,44,0.08)]">
                    <img
                      src={step.imageSrc}
                      alt={step.imageAlt}
                      className="block h-auto w-full"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                  </figure>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section
        className="mt-16 rounded-3xl border-2 border-dashed border-ink/15 bg-white/50 px-6 py-8 text-center sm:mt-20 sm:px-10"
        aria-label="Get started"
      >
        <h2 className="font-display text-2xl font-semibold text-ink">
          Ready for calmer mornings?
        </h2>
        <p className="mx-auto mt-2 max-w-lg font-sans text-sm leading-relaxed text-ink/70">
          Sign in to set up your school, or peek at plans when you are ready.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex rounded-full bg-skysoft/80 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft"
          >
            Sign in on Home
          </Link>
          <Link
            to="/pricing"
            className="inline-flex rounded-full bg-white px-5 py-2.5 font-sans text-sm font-semibold text-ink/80 ring-2 ring-ink/10 transition hover:bg-paper hover:text-ink"
          >
            See pricing
          </Link>
        </div>
      </section>
    </main>
  );
};

export default HowItWorksPage;
